import { Injectable } from '@angular/core';
import * as Cesium from 'cesium';
import { UnitMapEntity } from './unit-map-entity.service';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { UserProfileAttribute } from './user-profile-attribute.service';
import { WindowService } from './window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataContextService } from './data-context.service';
import { TimelineService } from './timeline.service';
 
export interface DeviceUpdateData {
  updatedAt: string;
  status: string;
  position: {
    coordinates: [number, number];
  };
  altitude?: number;
  speed: number;
  direction: number;
  createdAt: string;
}
 
@Injectable({
  providedIn: 'root'
})
export class DeviceMapEntityService {
  constructor() {}
}
 
export class DeviceMapEntity extends UnitMapEntity {
  // Constants for device states
  public readonly SAFE_STATE = 'SAFE';
  public readonly UNSAFE_STATE = 'DANGER';
  public readonly UNKNOWN_STATE = 'unknown';
  public readonly PROCALL_ACTIVE = 'procall';
  public readonly SUSPECT_STATE = 'SUSPECT';

 
  // Device properties
  public arcAngelId: string;
  public lastSafetyStatus: string;
  public statusSamples: Cesium.SampledProperty;
  public eventId: string;
  public procallState: boolean = false;
  public procallId: string | undefined;
  public calendarState: boolean = false;
  public missed: boolean = false;
  public calendar: any;
  public associatedPerimeter: any;
  public currentAltitude: number;
  public closestBeacon: any;
  public closestBeaconSample: Cesium.SampledProperty;
  public isPlayback: boolean;
  public lastLocationUpdatedAt: string;
  public userType: string | undefined;
  public profileId: string | undefined;
  public userDto: any;
  public activeTrackingState: boolean = false;
  public activeTrackingEvent: any;
  public trackingMissed: boolean = false;
  public trackingInDistress: boolean = false;
  public incidentState: boolean = false;
 
  constructor(
    id: string,
    safetyStatus: string,
    coordinates: [number, number],
    startAltitude: number | undefined,
    createdAt: string,
    eventId: string,
    playback: boolean,
    showStats: boolean = false,
    speed: number = 0.0,
    heading: number = 0.0,
    cameraService: CameraService,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService
  ) {
    const entityId = playback ? `arc-angel-playback-${id}` : `arc-angel-${id}`;
    const truncatedId = DeviceMapEntity.truncateString(id, 25);
   
    super(
      truncatedId,
      'ArcAngel//Devices',
      undefined, // icon will be set by getIconFromStatus
      showStats,
      120, // pathResolution
      cameraService,
      coreService,
      windowService,
      dataContext,
      modalService,
      timelineService
    );
 
    this.windowId += '-DeviceMapEntity'; // indicate this is a user profile window
    this.arcAngelId = id;
    this.lastSafetyStatus = safetyStatus;
    this.eventId = eventId;
    this.isPlayback = playback;
    this.lastLocationUpdatedAt = createdAt;
 
    // Initialize status samples
    this.statusSamples = this.createStatusSampleProperty();
    this.statusSamples.addSample(
      Cesium.JulianDate.fromDate(new Date(Date.parse(createdAt))),
      DeviceMapEntity.statusToCode(safetyStatus)
    );
 
    // Set altitude
    this.currentAltitude = startAltitude !== undefined ? startAltitude : 0;
 
    // Initialize closest beacon tracking
    this.closestBeaconSample = this.createStringSampleProperty();
 
    // Set initial icon based on status
    this.setIcon(this.getIconFromStatus(safetyStatus));
 
    // Add initial status and movement data
    this.addStatus(this.createUnitStatus(coordinates[0], coordinates[1], this.currentAltitude, Date.parse(createdAt)));
    this.addSpeedAndHeading({
      speed: speed,
      heading: heading,
      timestamp: new Date(createdAt)
    });
 
    // Add attributes and commands
    this.addAttribute(new UserProfileAttribute(this.id, this));
    // this.addCommand(new SendDeviceAMessageCommand());
  }
 
  private static truncateString(str: string, maxLength: number): string {
    return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
  }
 
  private createStringSampleProperty(): Cesium.SampledProperty {
  return new Cesium.SampledProperty(String);
}
 private createStatusSampleProperty(): Cesium.SampledProperty {
  return new Cesium.SampledProperty(Number);
}
  getEventId(): string {
    return this.eventId;
  }
 
  setEventId(eventId: string): void {
    this.eventId = eventId;
  }
 
  getIconFromStatus(deviceStatus: string): string {
    if (this.calendarState) {
      if (this.missed) {
        return 'assets/img/arcangel/calendar_user_missed.png';
      }
      return 'assets/img/arcangel/calendar_user.png';
    }
 
    if (this.activeTrackingState) {
      if (this.trackingInDistress) {
        return 'assets/img/arcangel/procall_user.png';
      } else if (this.trackingMissed) {
        return 'assets/img/arcangel/active_tracking_missed.png';
      }
      return 'assets/img/arcangel/active_tracking_blue.png';
    }
 
    if (this.procallState) {
      return 'assets/img/arcangel/procall_user.png';
    }
 
    if (this.userType !== undefined) {
      switch (this.userType) {
        case 'ems':
          return 'assets/img/arcangel/unknown_gray_ems.png';
        case 'fire':
          return 'assets/img/arcangel/unknown_gray_fire.png';
        case 'police':
          return 'assets/img/arcangel/unknown_gray_police.png';
        case 'traveler':
          return 'assets/img/arcangel/traveler_user.png';
        case 'other':
          return 'assets/img/arcangel/fr_other.png';
      }
    }
 
    const statusStr = (deviceStatus || '').toUpperCase();
    if (statusStr === 'SAFE') {
      return 'assets/img/arcangel/safe_user.png';
    } else if (statusStr === 'DANGER' || statusStr === 'UNSAFE' || statusStr === 'ACTIVE' || statusStr === '2') {
      return 'assets/img/arcangel/unsafe_user.png';
    } else if (statusStr === 'SUSPECT' || statusStr === '3') {
      return 'assets/img/arcangel/unknown_gray.png';
    } else {
      return 'assets/img/arcangel/unknown_user.png';
    }
  }
 
  setState(deviceStatus: string, date?: string): void {
    this.lastSafetyStatus = deviceStatus;
    this.setIcon(this.getIconFromStatus(deviceStatus));

    if (date) {
      this.statusSamples.addSample(
        Cesium.JulianDate.fromDate(this.roundDateToSeconds(new Date(date))),
        DeviceMapEntity.statusToCode(deviceStatus)
      );
    }

    if (this.procallState || this.calendarState || this.activeTrackingState) {
      this.show();
    }
  }
 
  private roundDateToSeconds(date: Date): Date {
    const rounded = new Date(date);
    rounded.setMilliseconds(0);
    return rounded;
  }
 
  getState(julianDate?: Cesium.JulianDate): string {
    if (julianDate) {
      const code = this.statusSamples.getValue(julianDate);
      return DeviceMapEntity.codeToStatus(code);
    }
    return this.lastSafetyStatus;
  }
 
  isSafe(julianDate?: Cesium.JulianDate): boolean {
    if (julianDate) {
      return this.statusSamples.getValue(julianDate) === this.SAFE_STATE;
    }
    return this.lastSafetyStatus === this.SAFE_STATE;
  }
 
  isUnSafe(julianDate?: Cesium.JulianDate): boolean {
    if (julianDate) {
      return this.statusSamples.getValue(julianDate) === this.UNSAFE_STATE;
    }
    return this.lastSafetyStatus === this.UNSAFE_STATE;
  }
 
  isUnKnown(julianDate?: Cesium.JulianDate): boolean {
    if (julianDate) {
      return this.statusSamples.getValue(julianDate) === this.UNKNOWN_STATE;
    }
    return this.lastSafetyStatus === this.UNKNOWN_STATE;
  }
 
  isFirstResponder(): boolean {
    return this.userType === 'fire' ||
           this.userType === 'ems' ||
           this.userType === 'police' ||
           this.userType === 'other';
  }
 
  getSafetyStatus(julianDate?: Cesium.JulianDate): string {
    if (julianDate) {
      return this.statusSamples.getValue(julianDate);
    }
    return this.lastSafetyStatus;
  }
 
  getUserType(): string | undefined {
    return this.userType;
  }
 
  getArcAngelId(): string {
    return this.arcAngelId;
  }
 
  setProfileId(profileId: string): void {
    this.profileId = profileId;
  }
 
  getProfileId(): string | undefined {
    return this.profileId;
  }
 
  setUserDto(userDto: any): void {
    if (userDto) {
      if (this.userDto) {
        this.userDto.setName(userDto.getName());
        this.userDto.setEmail(userDto.getEmail());
        this.userDto.setPermissions(userDto.getPermissions());
        this.userDto.setDevices(userDto.getDevices());
        this.userDto.setPhone(userDto.getPhone());
        this.userDto.setPro(userDto.getPro());
        this.userDto.setMembership(userDto.getMembership());
        this.userDto.setPrime(userDto.getPrime());
        this.userDto.setMedical(userDto.getMedical());
        this.userDto.setUserType(userDto.getUserType());
      } else {
        this.userDto = userDto;
      }
 
      this.name = userDto.getName();
 
      if (this.userType !== userDto.getUserType()) {
        this.userType = userDto.getUserType();
        this.refreshIcon();
      }
    }
  }
 
  getUserDto(): any {
    return this.userDto;
  }
 
  updateLocation(lon: number, lat: number, beacon: any, date: Date, alt?: number, speed?: number, direction?: number): void {
    const altitude = alt === null || alt === undefined ? this.currentAltitude : alt;
   
    this.addStatus(this.createUnitStatus(lon, lat, altitude, date.getTime()));
    this.addSpeedAndHeading({ speed: speed || 0, heading: direction || 0, timestamp: date });
   
    this.updateClosestBeacon(beacon, Cesium.JulianDate.fromDate(date));
  }
 
  update(data: DeviceUpdateData): void {
    if (data.updatedAt === this.lastLocationUpdatedAt && data.status === this.lastSafetyStatus) {
      return;
    }
 
    // Use moment.js equivalent or native Date
    const updatedAt = new Date(data.updatedAt).getTime() / 1000;
    const lastUpdate = this.endStatus ? this.endStatus.timestamp.getTime() / 1000 : 0;
    const date = Date.parse(data.updatedAt);
 
    if (updatedAt < lastUpdate) {
      const alt = data.altitude === null || data.altitude === undefined ? this.currentAltitude : data.altitude;
      this.addStatus(this.createUnitStatus(data.position.coordinates[0], data.position.coordinates[1], alt, date));
      this.addSpeedAndHeading({ speed: data.speed, heading: data.direction, timestamp: new Date(data.createdAt) });
      return;
    }
 
    const alt = data.altitude === null || data.altitude === undefined ? this.currentAltitude : data.altitude;
    if (data.altitude !== null && data.altitude !== undefined) {
      this.currentAltitude = data.altitude;
    }
 
    this.addStatus(this.createUnitStatus(data.position.coordinates[0], data.position.coordinates[1], alt, date));
    this.addSpeedAndHeading({ speed: data.speed, heading: data.direction, timestamp: new Date(data.createdAt) });
    this.setState(data.status, data.updatedAt);
    this.lastLocationUpdatedAt = data.updatedAt;
  }
 
  refreshIcon(): void {
    this.setIcon(this.getIconFromStatus(this.lastSafetyStatus));
  }
 
  // Procall state methods
  setProcallState(state: boolean): void {
    this.procallState = state;
    this.refreshIcon();
  }
 
  setProcallId(procallId: string): void {
    this.procallId = procallId;
  }
 
  isInProcallState(): boolean {
    return this.procallState;
  }
 
  getProcallId(): string | undefined {
    return this.procallId;
  }
 
  // Calendar state methods
  setCalendarState(state: boolean): void {
    this.calendarState = state;
    this.refreshIcon();
  }
 
  setCalendarEvent(calendar: any): void {
    this.calendar = calendar;
    this.missed = calendar.missed;
  }
 
  isInCalendarState(): boolean {
    return this.calendarState;
  }
 
  getCalendarEvent(): any {
    return this.calendar;
  }
 
  // Active tracking methods
  setActiveTrackingState(state: boolean): void {
    this.activeTrackingState = state;
    this.refreshIcon();
  }
 
  setActiveTrackingEvent(activeTrackingEvent: any): void {
    this.activeTrackingEvent = activeTrackingEvent;
    this.trackingMissed = activeTrackingEvent.missed;
    this.trackingInDistress = activeTrackingEvent.indistress;
    this.refreshIcon();
  }
 
  isInActiveTrackingState(): boolean {
    return this.activeTrackingState;
  }
 
  getActiveTrackingEvent(): any {
    return this.activeTrackingEvent;
  }
 
  setMissedTracking(value: boolean): void {
    if (value !== this.trackingMissed) {
      this.trackingMissed = value;
      this.refreshIcon();
    }
  }
 
  setInDistressTracking(value: boolean): void {
    if (value !== this.trackingInDistress) {
      this.trackingInDistress = value;
      this.refreshIcon();
    }
  }
 
  getMissedTracking(): boolean {
    return this.trackingMissed;
  }
 
  getInDistressTracking(): boolean {
    return this.trackingInDistress;
  }
 
  // Incident state methods
  isInsideAnIncident(): boolean {
    return this.incidentState;
  }
 
  setInsideAnIncident(incidentState: boolean): void {
    this.incidentState = incidentState;
    this.refreshIcon();
  }
 
  canBeGarbageCollected(): boolean {
    return !this.procallState && !this.incidentState;
  }
 
  // Altitude methods
  setCurrentAltitude(currentAltitude: number): void {
    this.currentAltitude = currentAltitude;
  }
 
  getCurrentAltitude(): number {
    return this.currentAltitude;
  }
 
  // Beacon methods
  updateClosestBeacon(closestBeacon: any, date?: Cesium.JulianDate): void {
    this.closestBeacon = closestBeacon;
    if (this.isPlayback && date) {
      const beacon = closestBeacon || 0;
      this.closestBeaconSample.addSample(date, beacon);
    }
  }
 
  getClosestBeacon(date?: Cesium.JulianDate): any {
    if (date) {
      return this.closestBeaconSample.getValue(date);
    }
    return this.closestBeacon;
  }
 
  // Perimeter methods
  setAssociatedPerimeter(perimeter: any): void {
    this.associatedPerimeter = perimeter;
  }
 
  getAssociatedPerimeter(): any {
    return this.associatedPerimeter;
  }
 
  // Override show methods for 2D display
  override getShowIn2d(): boolean {
    if (this.procallState || this.calendarState || this.activeTrackingState) {
      return true;
    }
    return false;
  }
 
  override setAddedToMap(status: boolean): void {
    if (!status && this.associatedPerimeter) {
      this.associatedPerimeter.removeTrackingDevice(this);
    }
  }
 
  // Add this mapping to your DeviceMapEntity class or as a utility
  private static statusToCode(status: string): number {
    const s = (status || '').toUpperCase();
    if (s === 'SAFE') return 1;
    if (s === 'DANGER' || s === 'UNSAFE' || s === 'ACTIVE') return 2;
    if (s === 'SUSPECT') return 5;
    if (s === 'PROCALL') return 4;
    return 3; // unknown
  }
 
 
  private static codeToStatus(code: number): string {
    switch (code) {
      case 1: return 'SAFE';
      case 2: return 'DANGER';
      case 5: return 'SUSPECT';
      case 4: return 'procall';
      default: return 'unknown';
    }
  }
}